import { useEffect, useState } from "react";

export type ScannerTier = "low" | "mixed" | "high";
export type FlagStatus = "bad" | "warn";
export type ScannerCategory = "established" | "new-launch" | "listed" | "hidden-gem" | "bonding";
export type Momentum = "surging" | "rising" | "flat" | "cooling" | "falling";

export interface ScannerFlag {
    label: string;
    status: FlagStatus;
    detail: string;
}

export interface ScannerSocial {
    type: string;
    url: string;
}

export interface ScannerSignals {
    buyPressureH1: number | null;
    buyPressureH24: number | null;
    momentum: Momentum;
    momentumDetail: string;
    listedOnDexscreener: boolean;
    freshness: "brand-new" | "recent" | "established";
}

export type TrendDir = "up" | "down" | "flat" | "unknown";
export interface ScannerTrends {
    holders: { dir: TrendDir; pct: number | null; from: number | null; now: number };
    volume: { dir: TrendDir; pct: number | null };
    price: { dir: TrendDir; pct: number | null };
    points: number;
    windowHours: number | null;
}
export interface ScannerAnalysis {
    posture: string;
    facts: string[];
}

// Structure ratios — turnover (24h volume per $ of liquidity) and backing
// (liquidity as % of market cap).
export interface ScannerRatios {
    volToLiq: number | null;
    liqToMcap: number | null;
}

// Aggregate chain mood computed server-side from the whole feed (facts, not a
// market call).
export interface ChainSentiment {
    label: "bullish" | "neutral" | "bearish";
    vwChange24h: number;
    gainersPct: number;
    avgBuyPressure: number | null;
    tokens: number;
}

// flap.sh launchpad enrichment — present only for tokens that originated on the
// Robinhood Chain launcher. `progress` is bonding-curve graduation % (>=100 =
// graduated to the DEX); taxes are the launchpad's buy/sell fee in basis points.
export interface ScannerFlap {
    onLaunchpad: boolean;
    progress: number | null;
    graduated: boolean;
    buyTaxBps: number | null;
    sellTaxBps: number | null;
    isLowRisk: boolean;
    isInnovation: boolean;
}

export interface ScannerToken {
    token: string;
    pair: string;
    block: number;
    symbol: string;
    name: string | null;
    logo: string | null;
    socials: ScannerSocial[];
    priceUsd: number | null;
    marketCap: number | null;
    fdv: number | null;
    liquidityUsd: number | null;
    volumeH24: number | null;
    priceChangeH24: number | null;
    priceChangeH1: number | null;
    txnsH24: number | null;
    ageHours: number | null;
    score: number;
    tier: ScannerTier;
    tierLabel: string;
    category: ScannerCategory;
    signals: ScannerSignals;
    trends: ScannerTrends;
    analysis: ScannerAnalysis;
    flap: ScannerFlap | null;
    holderCount: number | null;
    topHolderPct: number | null;
    top10HolderPct: number | null;
    criticalFlags: string[];
    headlineFlags: ScannerFlag[];
    factorCount: number;
    dexScreenerUrl: string;
    ratios: ScannerRatios;
    pick: boolean;          // one of the up-to-3 "Radar Pick" highlights
    flapUrl: string;        // launchpad page (bonding tokens trade there, not the DEX)
    launchpad: string;      // which launchpad a bonding token lives on ("flap.sh" | "pons")
}

export interface RadarPricing {
    unlockUsd: number;
    piggyUsd: number;
    ethUsd: number;
    stakePiggy: number;   // whole PIGGY tokens to stake for access
    ethAmount: number;    // ETH to pay for access
    creatorWallet: string;
}

export interface ScannerFeed {
    feed: ScannerToken[];
    trending: string[];
    counts: { established: number; newLaunch: number; listed: number; hiddenGem: number; bonding: number };
    pricing: RadarPricing | null;
    sentiment: ChainSentiment | null;
    locked: boolean;
    scannedAt: number;
    chainId: number;
    count: number;
}

const EMPTY: ScannerFeed = {
    feed: [], trending: [], counts: { established: 0, newLaunch: 0, listed: 0, hiddenGem: 0, bonding: 0 },
    pricing: null, sentiment: null, locked: false, scannedAt: 0, chainId: 0, count: 0,
};

// The scanner-feed function is edge-fetched and can time out on a cold Netlify
// container. Sanitize every field at the boundary — this payload becomes href
// values and rendered text, so a malformed/MITM'd response must not crash the
// React tree or inject a bad href. Mirrors the hardening in useLeaderboards.
const isAddr = (s: unknown): s is string =>
    typeof s === "string" && /^0x[0-9a-fA-F]{40}$/.test(s);

const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;

const httpsUrl = (v: unknown): string => {
    if (typeof v !== "string") return "";
    try {
        return new URL(v).protocol === "https:" ? v : "";
    } catch {
        return "";
    }
};

const asTier = (v: unknown): ScannerTier =>
    v === "low" || v === "high" ? v : "mixed";

const asCategory = (v: unknown): ScannerCategory =>
    v === "established" || v === "new-launch" || v === "hidden-gem" || v === "bonding" ? v : "listed";

const asMomentum = (v: unknown): Momentum =>
    v === "surging" || v === "rising" || v === "cooling" || v === "falling" ? v : "flat";

const asSocials = (v: unknown): ScannerSocial[] => {
    if (!Array.isArray(v)) return [];
    return v.flatMap((raw) => {
        const x = raw as Record<string, unknown>;
        const url = httpsUrl(x?.url);
        if (!url) return [];
        return [{ type: typeof x.type === "string" ? x.type : "link", url }];
    });
};

const asDir = (v: unknown): TrendDir =>
    v === "up" || v === "down" || v === "flat" ? v : "unknown";

const asTrends = (v: unknown): ScannerTrends => {
    const x = (v ?? {}) as Record<string, any>;
    const leg = (o: any) => ({ dir: asDir(o?.dir), pct: num(o?.pct) });
    return {
        holders: { ...leg(x.holders), from: num(x.holders?.from), now: num(x.holders?.now) ?? 0 },
        volume: leg(x.volume),
        price: leg(x.price),
        points: num(x.points) ?? 0,
        windowHours: num(x.windowHours),
    };
};

const asAnalysis = (v: unknown): ScannerAnalysis => {
    const x = (v ?? {}) as Record<string, unknown>;
    return {
        posture: typeof x.posture === "string" ? x.posture : "",
        facts: Array.isArray(x.facts) ? x.facts.filter((s): s is string => typeof s === "string") : [],
    };
};

const asFlap = (v: unknown): ScannerFlap | null => {
    if (!v || typeof v !== "object") return null;
    const x = v as Record<string, unknown>;
    return {
        onLaunchpad: Boolean(x.onLaunchpad),
        progress: num(x.progress),
        graduated: Boolean(x.graduated),
        buyTaxBps: num(x.buyTaxBps),
        sellTaxBps: num(x.sellTaxBps),
        isLowRisk: Boolean(x.isLowRisk),
        isInnovation: Boolean(x.isInnovation),
    };
};

const asSignals = (v: unknown): ScannerSignals => {
    const x = (v ?? {}) as Record<string, unknown>;
    return {
        buyPressureH1: num(x.buyPressureH1),
        buyPressureH24: num(x.buyPressureH24),
        momentum: asMomentum(x.momentum),
        momentumDetail: typeof x.momentumDetail === "string" ? x.momentumDetail : "",
        listedOnDexscreener: Boolean(x.listedOnDexscreener),
        freshness: x.freshness === "brand-new" || x.freshness === "established" ? x.freshness : "recent",
    };
};

const asFlags = (v: unknown): ScannerFlag[] => {
    if (!Array.isArray(v)) return [];
    return v.flatMap((raw) => {
        const x = raw as Record<string, unknown>;
        if (typeof x?.label !== "string") return [];
        return [{
            label: x.label,
            status: x.status === "bad" ? "bad" as const : "warn" as const,
            detail: typeof x.detail === "string" ? x.detail : "",
        }];
    });
};

const parseFeed = (raw: Record<string, unknown>): ScannerFeed => {
    const feed = (Array.isArray(raw.feed) ? raw.feed : []).flatMap((r) => {
        const x = r as Record<string, unknown>;
        if (!isAddr(x.token)) return [];
        const score = num(x.score);
        return [{
            token: x.token,
            // 40-hex address (V2 pair) or 64-hex Uniswap V4 pool ID — both are
            // valid DexScreener chart references.
            pair: typeof x.pair === "string" && /^0x[0-9a-fA-F]{40}([0-9a-fA-F]{24})?$/.test(x.pair) ? x.pair : "",
            block: num(x.block) ?? 0,
            symbol: typeof x.symbol === "string" ? x.symbol : "???",
            name: typeof x.name === "string" ? x.name : null,
            logo: httpsUrl(x.logo) || null,
            socials: asSocials(x.socials),
            priceUsd: num(x.priceUsd),
            marketCap: num(x.marketCap),
            fdv: num(x.fdv),
            liquidityUsd: num(x.liquidityUsd),
            volumeH24: num(x.volumeH24),
            priceChangeH24: num(x.priceChangeH24),
            priceChangeH1: num(x.priceChangeH1),
            txnsH24: num(x.txnsH24),
            ageHours: num(x.ageHours),
            score: score === null ? 0 : Math.max(0, Math.min(100, Math.round(score))),
            tier: asTier(x.tier),
            tierLabel: typeof x.tierLabel === "string" ? x.tierLabel : "",
            category: asCategory(x.category),
            signals: asSignals(x.signals),
            trends: asTrends(x.trends),
            analysis: asAnalysis(x.analysis),
            flap: asFlap(x.flap),
            holderCount: num(x.holderCount),
            topHolderPct: num(x.topHolderPct),
            top10HolderPct: num(x.top10HolderPct),
            criticalFlags: Array.isArray(x.criticalFlags)
                ? x.criticalFlags.filter((s): s is string => typeof s === "string")
                : [],
            headlineFlags: asFlags(x.headlineFlags),
            factorCount: num(x.factorCount) ?? 0,
            dexScreenerUrl: httpsUrl(x.dexScreenerUrl),
            ratios: {
                volToLiq: num((x.ratios as Record<string, unknown>)?.volToLiq),
                liqToMcap: num((x.ratios as Record<string, unknown>)?.liqToMcap),
            },
            pick: Boolean(x.pick),
            flapUrl: httpsUrl(x.flapUrl),
            launchpad: x.launchpad === "pons" ? "pons" : "flap.sh",
        }];
    });
    const rawCounts = (raw.counts ?? {}) as Record<string, unknown>;
    const rp = (raw.pricing ?? null) as Record<string, unknown> | null;
    const pricing: RadarPricing | null = rp ? {
        unlockUsd: num(rp.unlockUsd) ?? 0,
        piggyUsd: num(rp.piggyUsd) ?? 0,
        ethUsd: num(rp.ethUsd) ?? 0,
        stakePiggy: num(rp.stakePiggy) ?? 0,
        ethAmount: num(rp.ethAmount) ?? 0,
        creatorWallet: isAddr(rp.creatorWallet) ? rp.creatorWallet : "",
    } : null;
    const rs = (raw.sentiment ?? null) as Record<string, unknown> | null;
    const sentiment: ChainSentiment | null = rs ? {
        label: rs.label === "bullish" || rs.label === "bearish" ? rs.label : "neutral",
        vwChange24h: num(rs.vwChange24h) ?? 0,
        gainersPct: num(rs.gainersPct) ?? 0,
        avgBuyPressure: num(rs.avgBuyPressure),
        tokens: num(rs.tokens) ?? 0,
    } : null;
    return {
        feed,
        trending: Array.isArray(raw.trending) ? raw.trending.filter(isAddr) : [],
        counts: {
            established: num(rawCounts.established) ?? 0,
            newLaunch: num(rawCounts.newLaunch) ?? 0,
            listed: num(rawCounts.listed) ?? 0,
            hiddenGem: num(rawCounts.hiddenGem) ?? 0,
            bonding: num(rawCounts.bonding) ?? 0,
        },
        pricing,
        sentiment,
        locked: Boolean(raw.locked),
        scannedAt: num(raw.scannedAt) ?? 0,
        chainId: num(raw.chainId) ?? 0,
        count: num(raw.count) ?? feed.length,
    };
};

// Instant warm-start: the last good payload is kept in localStorage so a
// returning visitor sees the feed IMMEDIATELY while a fresh copy loads
// silently. index.html also prefetches the feed during HTML parse (in
// parallel with the JS bundles) and parks the promise on window.__radarFeed.
const LS_KEY = "piggy-radar-feed-v1";
const LS_MAX_AGE_MS = 30 * 60_000;

declare global {
    interface Window { __radarFeed?: Promise<Record<string, unknown> | null> }
}

function readWarmStart(): ScannerFeed | null {
    try {
        const c = JSON.parse(localStorage.getItem(LS_KEY) ?? "null") as { at: number; raw: Record<string, unknown> } | null;
        if (c && typeof c.at === "number" && Date.now() - c.at < LS_MAX_AGE_MS && c.raw) {
            return parseFeed(c.raw);
        }
    } catch { /* corrupt/absent cache */ }
    return null;
}

function saveWarmStart(raw: Record<string, unknown>): void {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ at: Date.now(), raw })); }
    catch { /* quota/private mode — purely an optimization */ }
}

export function useScannerFeed(token?: string | null): {
    data: ScannerFeed;
    loading: boolean;
    error?: string;
} {
    // Lazy initializer: last session's feed renders on the very first paint.
    const [data, setData] = useState<ScannerFeed>(() => readWarmStart() ?? EMPTY);
    const [loading, setLoading] = useState(() => data.feed.length === 0 && !data.locked);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let active = true;

        const fetchOnce = async (attempt = 0): Promise<Response> => {
            try {
                // The first (cold) scan computes on-demand and can take ~20-30s;
                // warm hits are instant (cached). Give the cold path room so the
                // browser doesn't abandon a scan that's about to return.
                const res = await fetch("/.netlify/functions/scanner-feed", {
                    signal: AbortSignal.timeout(45_000),
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res;
            } catch (e) {
                if (attempt < 2) {
                    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
                    return fetchOnce(attempt + 1);
                }
                throw e;
            }
        };

        const fetchData = async () => {
            try {
                // Consume the index.html prefetch exactly once (only valid for
                // anonymous requests — with a session token, fetch fresh so
                // the entitled payload is served).
                let raw: Record<string, unknown> | null = null;
                if (!token && window.__radarFeed) {
                    raw = await window.__radarFeed;
                    window.__radarFeed = undefined;
                }
                if (!raw) {
                    const res = await fetchOnce();
                    raw = (await res.json()) as Record<string, unknown>;
                }
                if (!active) return;
                setError(undefined);
                setData(parseFeed(raw));
                setLoading(false);
                saveWarmStart(raw);
            } catch (e) {
                if (!active) return;
                // Keep last-good data on screen; only surface the error banner.
                setError((e as Error).message);
                setLoading(false);
            }
        };

        fetchData();
        const id = setInterval(fetchData, 60_000); // refresh every minute
        return () => {
            active = false;
            clearInterval(id);
        };
    }, [token]); // refetch when a session token appears (after unlock)

    return { data, loading, error };
}
